import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { trackPageView } from '../lib/analytics';
import { trackAppEvent } from '../lib/app-analytics';

function actorTypeForPath(pathname: string): "admin" | "artist" | "client" | "anon" {
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/artist') || pathname.startsWith('/manage')) return 'artist';
  if (pathname.startsWith('/client') || pathname.startsWith('/portal') || pathname.startsWith('/healing')) return 'client';
  return 'anon';
}

function shouldEmitAppPageView(pathname: string): boolean {
  return (
    pathname.startsWith('/admin') ||
    pathname.startsWith('/artist') ||
    pathname.startsWith('/manage')
  );
}

function redactPath(pathname: string): string {
  // Strip token-like trailing segments to keep PII out
  return pathname.replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, '/:token');
}

function actorIdForPath(pathname: string): string | undefined {
  // Use the first long token segment in the URL as a stable per-session
  // actor identifier. We hash by taking only the first 8 chars so the
  // actorId never round-trips a usable secret.
  const m = pathname.match(/\/([A-Za-z0-9_-]{20,})(?=\/|$)/);
  if (!m) return undefined;
  return `token:${m[1].slice(0, 8)}`;
}

export const useAnalytics = () => {
  const [location] = useLocation();
  const prevLocationRef = useRef<string>(location);
  const hasFiredInitial = useRef(false);

  // Fire exactly one page_view on the initial mount so GA4 records the
  // correct landing page for ad-click sessions. Without this, the first
  // session_start has no associated page_view and GA reports "(not set)".
  useEffect(() => {
    if (!hasFiredInitial.current) {
      hasFiredInitial.current = true;
      trackPageView(redactPath(location));
      if (shouldEmitAppPageView(location)) {
        trackAppEvent({
          eventName: 'page_view',
          actorType: actorTypeForPath(location),
          actorId: actorIdForPath(location),
          properties: { path: redactPath(location) },
        });
      }
      prevLocationRef.current = location;
    }
  }, []);

  // Subsequent SPA navigations — only fires when the URL actually changes.
  useEffect(() => {
    if (location !== prevLocationRef.current) {
      trackPageView(redactPath(location));
      if (shouldEmitAppPageView(location)) {
        trackAppEvent({
          eventName: 'page_view',
          actorType: actorTypeForPath(location),
          actorId: actorIdForPath(location),
          properties: { path: redactPath(location) },
        });
      }
      prevLocationRef.current = location;
    }
  }, [location]);
};
